import { Component, OnInit, OnChanges, SimpleChanges, ElementRef, Input, Output, EventEmitter, HostListener, OnDestroy } from '@angular/core';
import * as leaflet from 'leaflet';
import { addControlPlaceholders } from './leaflet.service';
import { Bounds } from 'map-wald';
import { BasemapDescriptor, MapExtentConfiguration } from './data';
import { icon, Marker } from 'leaflet';

// const DEFAULT_BASE_MAP='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const CLICK_DEBOUNCE_MS = 250;

const iconRetinaUrl = 'assets/marker-icon-2x.png';
const iconUrl = 'assets/marker-icon.png';
const shadowUrl = 'assets/marker-shadow.png';
const iconDefault = icon({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});
Marker.prototype.options.icon = iconDefault;


@Component({
  selector: 'leaflet-map',
  template: `<div class="leafletHost" [style]="styles">
  <ng-content></ng-content>
</div>
`, styles: [
    `
  .leafletHost{
    width:100%;
    min-height:400px;
  }`
  ]
})
export class LeafletMapComponent implements OnInit, OnChanges, OnDestroy {
  @Input() bounds: Bounds;
  @Input() centre: leaflet.LatLngExpression;
  @Input() maxBounds: Bounds;
  @Input() baseMap: BasemapDescriptor;
  @Input() zoomControl = true;
  @Input() minZoom = 5;
  @Input() maxZoom = 32;
  @Input() zoom = 5;
  @Input() zoomAnimation = true;
  @Input() pointSelection = false;
  @Input() doubleClickZoom = true;
  @Input() scaleBar = false;
  @Output() mapCreated = new EventEmitter<leaflet.Map>();
  @Output() pointSelected = new EventEmitter<leaflet.LatLng>();
  @Output() pointDoubleClicked = new EventEmitter<leaflet.LatLng>();
  @Output() boundsChanged = new EventEmitter<Bounds>();
  @Output() zoomChange = new EventEmitter<number>();
  visibleBoundingBox: leaflet.Rectangle;
  visibleBoundingBoxTimeout: number=-1;
  scaleControl: leaflet.Control.Scale;


  get layerCount(): number {
    if(!this.map){
      return 0;
    }
    let count = 0;
    this.map.eachLayer(function(){ count += 1; });
    return count;
  }

  get layerNames(): string[] {
    if(!this.map){
      return [];
    }
    let result:string[] = [];
    let last:string = null;
    let lastCount = 0;
    this.map.eachLayer((l:any)=>{
      const lbl = l._url||'-';
      if(lbl===last){
        lastCount+=1;
        result.pop();
        result.push(`${lbl} (${lastCount})`);
      } else {
        result.push(lbl);
        last = lbl;
        lastCount = 1;
      }
    });
    //
    return result;
  }

  map: leaflet.Map;
  styles: any = {};
  initialised = false;

  private handlingClick = false;
  // Leaflet.TileLayer
  private baseLayer: leaflet.TileLayer;

  constructor(private element: ElementRef) {
  }

  ngOnDestroy(): void {
    this.destroyMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialised) {
      return;
    }

    const changeCount = Object.keys(changes).length;

    // if(this.creating){
    //   return;
    // }

    // if(this.map){
    //   if(changes.markers){
    //     this.setupMarkers();

    //     if(changeCount===1){
    //       return;
    //     }
    //   }
    //   this.map.remove();
    // }

    //    if(!this.creating){
    this.updateMap(changes);
    //    }

    if (changes.bounds?.currentValue&&!boundsEqual(changes.bounds.previousValue,changes.bounds.currentValue)) {
        this.setBounds();
    }

    if (changes.centre) {
      setTimeout(() => {
        this.map.flyTo(this.centre,this.zoom,{
          animate:false
        });
      });
    }
  }

  ngOnInit(): void {
    this.updateMap();
    this.setBounds();
  }

  applyExtent(mapExtent: MapExtentConfiguration,notify:boolean=false):void {
    setTimeout(()=>{
      if(!mapExtent||!this.map){
        return;
      }
      if(mapExtent.lat!==undefined && mapExtent.lng!==undefined && mapExtent.zoom!==undefined){
        this.centre = [+mapExtent.lat,+mapExtent.lng];
        this.zoom = +mapExtent.zoom;
        this.map?.setView([+mapExtent.lat,+mapExtent.lng],+mapExtent.zoom);
      }
      if(mapExtent.west!==undefined && mapExtent.east!==undefined && mapExtent.north!==undefined && mapExtent.south!==undefined){
        this.bounds = {
          west:mapExtent.west,
          east:mapExtent.east,
          north:mapExtent.north,
          south:mapExtent.south
        };
        this.map?.fitBounds([[mapExtent.south,mapExtent.west],[mapExtent.north,mapExtent.east]]);
      }

      if(notify){
        this.boundsChanged.emit(this.currentBounds());
      }
    })
  }

  showBoundingBox(bounds:Bounds,timeout?:number){
    this.removeBoundingBox();

    if(!bounds){
      return;
    }

    this.visibleBoundingBox = leaflet.rectangle([
        [bounds.south, bounds.west],
        [bounds.north, bounds.east]
      ], {
      color: '#000',
      weight: 3,
      fillOpacity: 0.2
    }).addTo(this.map);
    this.visibleBoundingBox.getPane().style.zIndex = '10000';
    if(timeout){
      if(this.visibleBoundingBoxTimeout !== -1){
        clearTimeout(this.visibleBoundingBoxTimeout);
      }
      this.visibleBoundingBoxTimeout = setTimeout(() => {
        this.removeBoundingBox();
        this.visibleBoundingBoxTimeout = -1;
      }, timeout);
    }
  }

  removeBoundingBox() {
    if(this.visibleBoundingBox){
      this.visibleBoundingBox.removeFrom(this.map);
      this.visibleBoundingBox = null;
    }
  }

  destroyMap() {
    if(this.map){
      this.map.off();
      this.map.remove();
      this.map = null;
    }
  }

  updateMap(changes?: SimpleChanges): void {
    setTimeout(() => {
      if (!this.map) {
        this.createMap();
        return;
      }

      this.ensureMapSize();
      if (changes && changes.baseMap) {
        this.updateBaseMap();
      }

      if (changes?.zoom&&!changes?.centre) {
        this.map.setZoom(this.zoom);
      }

      if(changes?.scaleBar){
        if(this.scaleBar){
          this.scaleControl = leaflet.control.scale({
            imperial:false,
            metric:true,
            maxWidth:400
          });
          this.map.addControl(this.scaleControl);
        } else {
          this.scaleControl.remove();
          // this.map.removeControl(this.scaleControl);
        }
      }
    });

    // Update parameters
  }

  updateBaseMap() {
    if (!this.baseMap?.urlTemplate) {
      if (this.baseLayer) {
        this.baseLayer.removeFrom(this.map);
        this.baseLayer = null;
      }
      return;
    }

    if (this.baseLayer) {
      this.baseLayer.setUrl(this.baseMap.urlTemplate);
      return;
    }

    const options: leaflet.TileLayerOptions = {};
    if (this.baseMap.maxNativeZoom) {
      options.maxNativeZoom = this.baseMap.maxNativeZoom;
    }

    this.baseLayer = leaflet.tileLayer(this.baseMap.urlTemplate, options);

    if (this.map) {
      this.baseLayer.addTo(this.map).bringToBack();
    }
  }

  createMap(): void {
    setTimeout(() => {
      if (this.map) {
        this.destroyMap();
      }
      const theDiv = this.element.nativeElement as HTMLElement;
      const theHost = theDiv.querySelector('.leafletHost');
      // let baseLayers = R.mapObjIndexed(v=>{
      //   return L.tileLayer(v,
      //     { maxZoom: 18, attribution: '...' });
      // },this.baseMaps);

      // if(!this.baseMap || !baseLayers[this.baseMap]){
      //   this.baseMap = Object.keys(this.baseMaps)[0];
      // }

      // let baseLayerArray = [baseLayers[this.baseMap]];
      let crs = leaflet.CRS.EPSG3857;//:L.CRS.Simple;
      // if(this.crs){
      //   crs = L.CRS[this.crs];
      // }

      // let panes = 0;
      // if(this.map){
      //   panes = getCustomMapPanes(this.map).length;
      // }
      this.updateBaseMap();
      const baseLayerArray = [
      ];
      if (this.baseLayer) {
        baseLayerArray.push(this.baseLayer);
      }
      this.map = leaflet.map(theHost as HTMLElement, {
        crs,
        zoom: this.zoom,
        maxBounds: toLeafletBounds(this.maxBounds),
        minZoom: this.minZoom,
        maxZoom: this.maxZoom,
        zoomControl: this.zoomControl,
        center: leaflet.latLng(-20, 135),
        // zoom: this.zoom,
        // minZoom: this.minZoom,
        // maxZoom: this.maxZoom,
        scrollWheelZoom: true,
        layers: baseLayerArray,
        continuousWorld: false,
        worldCopyJump: true,
        noWrap: true,
        tap: false,
        zoomAnimation: this.zoomAnimation,
        doubleClickZoom: this.doubleClickZoom
        // worldCopyJump:true
        // attributionControl: this.attribution
      } as leaflet.MapOptions);
      addControlPlaceholders(this.map);
      // if(!this.pannable){
      //   this.map.dragging.disable();
      // }

      // if(!this.zoomable){
      //   this.map.touchZoom.disable();
      //   this.map.doubleClickZoom.disable();
      //   this.map.scrollWheelZoom.disable();
      // }

      // configureVectorPanes(panes,this.map);

      // this._helper.register(this.map);

      this.mapCreated.emit(this.map);

      this.map.on('click', (evt: leaflet.LeafletMouseEvent) => {
        if (this.handlingClick) {
          return;
        }

        this.handlingClick = true;
        setTimeout(() => {
          if (!this.handlingClick) {
            return;
          }
          this.handlingClick = false;

          if (!this.pointSelection || evt.originalEvent.defaultPrevented) {
            return;
          }

          this.pointSelected.emit(evt.latlng);
        }, CLICK_DEBOUNCE_MS);
      });

      this.map.on('dblclick', (evt: leaflet.LeafletMouseEvent) => {
        this.handlingClick = false; // cancel any pending clicks
        this.pointDoubleClicked.emit(evt.latlng);
      });

      this.map.on('moveend', (evt: leaflet.LeafletEvent) => {
        const bounds = this.currentBounds();
        this.boundsChanged.emit(bounds);
      });

      this.map.on('zoomend', (evt: leaflet.LeafletEvent) => {
        const bounds = this.currentBounds();
        this.boundsChanged.emit();
        this.zoom = this.map.getZoom();
        this.zoomChange.emit(this.zoom);
      });
      // this.creating=false;

      // this.map.on('zoomend',()=>this.coordinatesChanged());
      // this.map.on('moveend',()=>this.coordinatesChanged());

      // if(this.showLayerControl){
      //   this.layerControl = L.control.layers(baseLayers, [],{
      //     hideSingleBase:true
      //   }).addTo(this.map);
      // }

      // this.mapCreated.emit(this.map);



      // this.markerLayers = [];
      // this.setupMarkers();
      this.map.whenReady(() => {
        this.ensureMapSize();
      });

      this.setBounds();
      this.initialised = true;
    });

  }

  private ensureMapSize(): boolean {
    if(!this.map){
      return false;
    }
    let mapSize = this.map.getSize();
    if(mapSize.x>0&&mapSize.y>0){
      return true;
    }

    this.map.invalidateSize();
    mapSize = this.map.getSize();
    return mapSize.x>0&&mapSize.y>0;
  }

  setBounds(): void {
    if (!this.map || !this.bounds) {
      return;
    }
    if(!this.ensureMapSize()){
      setTimeout(()=>{
        this.setBounds();
      });
      return;
    }
    const bounds = toLeafletBounds(this.bounds);
    if(almostEqual(bounds[0][0], bounds[1][0]) || almostEqual(bounds[0][1], bounds[1][1])){
      return;
    }
    this.map.fitBounds(bounds);
  }

  currentBounds(): Bounds {
    if (!this.map) {
      return null;
    }

    const bounds = this.map.getBounds();
    return toMWBounds(bounds);
  }

  withMap(fn:((m:L.Map)=>void)): void {
    if(this.map){
      this.map.whenReady(()=>{
        fn(this.map);
      });
    } else {
      const subscription = this.mapCreated.subscribe((m: L.Map) => {
        fn(m);
        subscription.unsubscribe();
      });
    }
  }
}
/*
http://35.244.111.168:8080/wms
?service=WMS
&request=GetMap
&layers=wcf
&styles=
&format=image%2Fpng
&transparent=true
&version=1.1.1
&time=2019-01-01T00%3A00%3A00.000Z
&width=256
&height=256
&srs=EPSG%3A3857
&bbox=-17532819.79994059,-5009377.085697311,-15028131.257091936,-2504688.542848655

*/

export function toLeafletBounds(bounds: Bounds): L.LatLngBoundsLiteral {
  if (!bounds) {
    return null;
  }

  return [
    [bounds.south, bounds.west],
    [bounds.north, bounds.east]
  ];
}

export function toMWBounds(bounds: L.LatLngBounds): Bounds {
  if (!bounds) {
    return null;
  }

  return {
    south: bounds.getSouth(),
    west: bounds.getWest(),
    north: bounds.getNorth(),
    east: bounds.getEast()
  };
}

export function boundsEqual(previousValue: Bounds, currentValue: Bounds): boolean {
  if (!previousValue || !currentValue) {
    return false;
  }

  return previousValue.south === currentValue.south &&
    previousValue.north === currentValue.north &&
    previousValue.east === currentValue.east &&
    previousValue.west === currentValue.west;
}

function latLngToTuple(ll: leaflet.LatLngExpression): [number, number] {
  if (!ll) {
    return null;
  }

  if ((ll as any).lat) {
    return [(ll as any).lat, (ll as any).lng];
  }

  return ll as [number, number];
}

function latLngEqual(lhs: leaflet.LatLngExpression, rhs: leaflet.LatLngExpression): boolean {
  lhs = latLngToTuple(lhs);
  rhs = latLngToTuple(rhs);
  if (!lhs || !rhs) {
    return false;
  }

  return (lhs[0] === rhs[0]) && (lhs[1] === rhs[1]);
}

function almostEqual(arg0: number, arg1: number) :boolean {
  const diff = Math.abs(arg0 - arg1);
  return diff < 0.000001;
}

